import { IdentityUserAri, PlatformSiteAri } from '@atlassian/ari';
import { EntryPointRef } from '@atlassian/entry-point-types';
import { homeRecentsListEntryPoint } from '@atlassian/home-recents/entrypoint';
import { loadEntryPoint } from 'react-relay';

import { getDefaultTabKey } from '@townsquare/hometab-saved-tab';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { createResource } from '@townsquare/router/resources/createResource';
import type {
	RouteResource,
	RouterContext,
	RouterDataContext,
} from '@townsquare/router/routing-types';
import type { ResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';

import { isRecentsTab, parseRecentsTab, RECENTS_TAB_QUERY_PARAM, type RecentsTab } from '../types';

type HomeRecentsData = EntryPointRef<typeof homeRecentsListEntryPoint>;

/**
 * Undefined when this route has no recents list to preload — either it isn't a recents surface, or
 * the home tab will open on a tab other than worked on / viewed.
 */
const getRecentsActivityType = (
	routerContext: RouterContext | RouterDataContext,
): RecentsTab | undefined => {
	switch (routerContext.route.name) {
		case 'hometab': {
			const defaultTabKey = getDefaultTabKey();
			return isRecentsTab(defaultTabKey) ? defaultTabKey : undefined;
		}
		case 'recents':
			return parseRecentsTab(routerContext.query[RECENTS_TAB_QUERY_PARAM]);
		default:
			return undefined;
	}
};

export const getHomeRecentsResourceKey = (activityType: string): string =>
	`home-recents-resource-${activityType}`;

export const homeRecentsResource: RouteResource<HomeRecentsData | undefined> = createResource<
	HomeRecentsData | undefined
>({
	type: 'RECENTS',
	getKey: (routerContext) =>
		getHomeRecentsResourceKey(getRecentsActivityType(routerContext) ?? 'no-preload'),
	getData: async (routerContext, resourceContext) => {
		if (!fg('townsquare_enable_home_recents')) {
			return;
		}

		const activityType = getRecentsActivityType(routerContext);

		if (!activityType) {
			return;
		}

		const { user, collabContext } = resourceContext as ResourceContext;

		if (!user?.accountId) {
			return;
		}

		const rootContainerCloudIds =
			collabContext?.collabContextScoping?.collabContextCloudIds ??
			collabContext?.orgCloudIds ??
			[];
		const cloudId = collabContext?.rovoWorkspace;

		try {
			return loadEntryPoint({ getEnvironment: getAggRelayEnvironment }, homeRecentsListEntryPoint, {
				activityType,
				collabContextId:
					cloudId && fg('home-recents-root-container-ids')
						? PlatformSiteAri.create({ siteId: cloudId })
						: undefined,
				rootContainerIds: rootContainerCloudIds.map((siteId) => PlatformSiteAri.create({ siteId })),
				currentUser: IdentityUserAri.create({ userId: user.accountId }),
				first: 100,
			});
		} catch {
			return;
		}
	},
});
